import { useEffect, useState } from "react";
import type { Order } from "@/mocks/branch/orders";
import { PAYMENT_METHODS, PICKUP_METHODS, SOURCE_LABELS, vatBreakdown, hasAppFee } from "../constants";
import { printOrderReceipt } from "../printReceipt";
import { mockBranchInfo } from "@/mocks/branch/employee";
import { buildZatcaTlvBase64, buildOrderTimestamp, generateQrDataUrl } from "@/utils/zatcaQr";

interface InvoiceCardProps {
  order: Order;
  compact?: boolean;
}

export default function InvoiceCard({ order, compact = false }: InvoiceCardProps) {
  const { net, vat } = vatBreakdown(order.total);
  const payment = PAYMENT_METHODS[order.paymentMethod];
  const pickup = PICKUP_METHODS[order.pickupMethod];

  const [qrUrl, setQrUrl] = useState("");

  useEffect(() => {
    let active = true;
    const text = buildZatcaTlvBase64({
      sellerName: mockBranchInfo.commercialName,
      vatNumber: mockBranchInfo.taxNumber,
      timestamp: buildOrderTimestamp(order.createdAt),
      totalWithVat: order.total,
      vatTotal: vat,
    });
    generateQrDataUrl(text).then((url) => {
      if (active) setQrUrl(url);
    });
    return () => {
      active = false;
    };
  }, [order.id, order.total, order.createdAt, vat]);

  return (
    <div className="bg-background-50 rounded-card border border-background-200 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-background-200 bg-background-100">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-btn bg-primary-100 text-primary-700 flex items-center justify-center">
            <i className="ri-receipt-line text-lg"></i>
          </div>
          <h4 className="text-sm font-bold text-foreground-900">الفاتورة الإلكترونية المبسطة</h4>
        </div>
        <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-bold whitespace-nowrap">
          ضريبية مبسطة
        </span>
      </div>

      <div className="p-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1 min-w-0 space-y-1">
            <p className="text-sm font-bold text-foreground-900">{mockBranchInfo.commercialName}</p>
            <p className="text-xs text-foreground-500">
              الرقم الضريبي: <span dir="ltr" className="font-semibold">{mockBranchInfo.taxNumber}</span>
            </p>
            <p className="text-xs text-foreground-500">{mockBranchInfo.branchName} — {mockBranchInfo.address}</p>
          </div>
          <div
            className="w-24 h-24 rounded-btn border border-background-300 bg-white flex items-center justify-center overflow-hidden shrink-0"
            title="رمز QR بصيغة TLV/Base64 — يتضمن بيانات البائع والرقم الضريبي والختم الزمني والإجمالي والضريبة"
          >
            {qrUrl ? (
              <img
                src={qrUrl}
                alt={`رمز QR للفاتورة الضريبية رقم INV-${order.number}`}
                className="w-full h-full object-contain"
              />
            ) : (
              <i className="ri-qr-code-line text-4xl text-foreground-300"></i>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="rounded-btn bg-background-100 p-2.5">
            <p className="text-foreground-400">رقم الفاتورة</p>
            <p className="font-bold text-foreground-900 mt-0.5" dir="ltr">INV-{order.number}</p>
          </div>
          <div className="rounded-btn bg-background-100 p-2.5">
            <p className="text-foreground-400">التاريخ والوقت</p>
            <p className="font-bold text-foreground-900 mt-0.5" dir="ltr">{order.createdAt}</p>
          </div>
          <div className="rounded-btn bg-background-100 p-2.5">
            <p className="text-foreground-400">طريقة الاستلام</p>
            <p className="font-bold text-foreground-900 mt-0.5">{pickup.label}</p>
          </div>
          <div className="rounded-btn bg-background-100 p-2.5">
            <p className="text-foreground-400">وسيلة الدفع</p>
            <p className="font-bold text-foreground-900 mt-0.5">{payment.label}</p>
          </div>
        </div>

        <div className="border border-background-200 rounded-btn overflow-hidden">
          <div className="grid grid-cols-12 bg-background-100 px-3 py-2 text-[11px] font-bold text-foreground-500">
            <span className="col-span-6">المنتج</span>
            <span className="col-span-2 text-center">الكمية</span>
            <span className="col-span-2 text-center">السعر</span>
            <span className="col-span-2 text-left">الإجمالي</span>
          </div>
          <div className="divide-y divide-background-200">
            {order.items.map((item, idx) => (
              <div key={idx} className="grid grid-cols-12 px-3 py-2 text-xs items-center">
                <span className="col-span-6 text-foreground-800 truncate">{item.name}</span>
                <span className="col-span-2 text-center text-foreground-600">{item.quantity}</span>
                <span className="col-span-2 text-center text-foreground-600">{item.unitPrice.toFixed(2)}</span>
                <span className="col-span-2 text-left font-semibold text-foreground-900">
                  {(item.quantity * item.unitPrice).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between text-foreground-600">
            <span>المجموع الفرعي</span>
            <span>{order.subtotal.toFixed(2)} ر.س</span>
          </div>
          {order.discount > 0 ? (
            <div className="flex justify-between text-foreground-600">
              <span>الخصم</span>
              <span className="text-red-600">-{order.discount.toFixed(2)} ر.س</span>
            </div>
          ) : null}
          {hasAppFee(order) ? (
            <div className="flex justify-between text-foreground-600">
              <span>رسوم التطبيق</span>
              <span>{order.appFee.toFixed(2)} ر.س</span>
            </div>
          ) : null}
          {order.deliveryFee && order.deliveryFee > 0 ? (
            <div className="flex justify-between text-foreground-600">
              <span>رسوم التوصيل</span>
              <span>{order.deliveryFee.toFixed(2)} ر.س</span>
            </div>
          ) : null}
          <div className="flex justify-between text-foreground-600">
            <span>صافي المبلغ (قبل الضريبة)</span>
            <span>{net.toFixed(2)} ر.س</span>
          </div>
          <div className="flex justify-between text-foreground-600">
            <span>الضريبة المتضمنة (15%)</span>
            <span>{vat.toFixed(2)} ر.س</span>
          </div>
          <div className="flex justify-between font-bold text-foreground-900 text-base border-t border-dashed border-background-300 pt-2">
            <span>الإجمالي شامل الضريبة</span>
            <span>{order.total.toFixed(2)} ر.س</span>
          </div>
          <div className="space-y-1 pt-1">
            <p className="text-[11px] text-foreground-400 flex items-center gap-1">
              <i className="ri-information-line"></i>
              المصدر: {SOURCE_LABELS[order.source].label} — أسعار المنتجات شاملة ضريبة القيمة المضافة
            </p>
            <p className="text-[11px] text-foreground-400 flex items-center gap-1">
              <i className="ri-percent-line"></i>
              رسوم التطبيق تُحتسب على طلبات التطبيق فقط (لا تُحتسب على الطلبات المحلية داخل المطعم أو على طلبات الموظف)
            </p>
          </div>
        </div>

        {!compact ? (
          <div className="p-3 rounded-btn bg-secondary-50 border border-secondary-200 text-[11px] text-secondary-700 leading-relaxed">
            فاتورة ضريبية مبسطة تتضمن رمز QR وفق البيانات المعتمدة، ويتم توليدها داخل النظام دون
            ربط أو إرسال خارجي إلى منصة فاتورة.
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => printOrderReceipt(order)}
          className="w-full h-12 rounded-btn bg-primary-500 text-white text-sm font-bold flex items-center justify-center gap-2 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-printer-line text-lg"></i>
          </div>
          طباعة الفاتورة
        </button>
      </div>
    </div>
  );
}